import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { getTypeByCronPattern } from '../schedulingHelpers';
import '../../Automations/components/ScheduledAutomationSidebar/ScheduledAutomationSidebar.less';
import SelectDropdown from '@components/core/SelectDropdown';

const selectOptionsCache = new WeakMap();

const getSelectOptions = (values) => {
    if (!selectOptionsCache.has(values)) {
        selectOptionsCache.set(
            values,
            values.map(({ id, name }) => ({ id, text: name })),
        );
    }
    return selectOptionsCache.get(values);
};

const TimeBasedSchedulingFields = ({
    options,
    frequencies,
    visible,
    value: patterns,
    onChange,
    readOnly = false,
}) => {
    const patternsRef = useRef(patterns);
    patternsRef.current = patterns;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        if (readOnly || !visible || !options || !frequencies?.length) {
            return;
        }
        if (patterns?.length) {
            return;
        }
        const firstKey = Object.keys(options)[0];
        const defaultValue = options[firstKey]?.default;
        if (defaultValue) {
            onChange([defaultValue]);
        }
    }, [patterns, visible, options, frequencies, onChange, readOnly]);

    const scheduleTypes = useMemo(() => {
        if (!options || !frequencies?.length) {
            return [];
        }
        const allowed = new Set(Object.keys(options));
        return frequencies
            .filter(({ id }) => allowed.has(id))
            .map(({ id, name }) => ({ id, text: name }));
    }, [options, frequencies]);

    const handleTypeChange = useCallback(
        (parentIndex, nextType) => {
            const currentType = getTypeByCronPattern(patternsRef.current[parentIndex]);
            if (nextType === currentType || !options?.[nextType]) {
                return;
            }
            const updatedPatterns = structuredClone(patternsRef.current);
            updatedPatterns[parentIndex] = options[nextType].default;
            onChangeRef.current(updatedPatterns);
        },
        [options],
    );

    if (!visible || !options || !frequencies?.length) {
        return null;
    }

    return (patterns ?? []).map((pattern, parentIndex) => {
        const type = getTypeByCronPattern(pattern);
        const dropdowns = options[type]?.options ?? [];

        return (
            <div key={`pattern-${parentIndex}`} className="auto-sched-time-row">
                <div key={`type-${parentIndex}`} className="auto-sched-time-cell">
                    <SelectDropdown
                        id={`auto-sched-type-${parentIndex}`}
                        name="type"
                        className="auto-sched-select automation-field automation-field--type"
                        value={type}
                        disabled={readOnly}
                        onChange={(v) => handleTypeChange(parentIndex, v)}
                        options={scheduleTypes}
                        searchable={false}
                    />
                </div>
                {dropdowns.map(({ index, name, values }) => (
                    <div key={`dropdown-${parentIndex}-${index}-${name}`} className="auto-sched-time-cell">
                        <SelectDropdown
                            id={`auto-sched-${name}-${parentIndex}`}
                            name={name}
                            className={`auto-sched-select automation-field automation-field--${name}`}
                            value={pattern[index] ?? null}
                            disabled={readOnly}
                            onChange={(v) => {
                                if (v == null) return;
                                const updatedPatterns = structuredClone(patternsRef.current);
                                const nextPattern = [...updatedPatterns[parentIndex]];
                                nextPattern[index] = String(v);
                                updatedPatterns[parentIndex] = nextPattern;
                                onChangeRef.current(updatedPatterns);
                            }}
                            options={getSelectOptions(values)}
                            searchable={false}
                        />
                    </div>
                ))}
            </div>
        );
    });
};

export default TimeBasedSchedulingFields;
